import React from "react";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { Skeleton } from "~/components/ui/skeleton";
import { useSalesConditions } from "~/hooks/useSalesConditions";
import { SalesCondition } from "~/types/client";

interface SalesConditionSelectorProps {
  value: string | undefined;
  onChange: (value: string) => void;
  label?: string;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  includeInheritOption?: boolean;
  inheritLabel?: string;
}

const SalesConditionSelector: React.FC<SalesConditionSelectorProps> = ({
  value,
  onChange,
  label = "Condición de Venta",
  placeholder = "Seleccionar condición",
  className = "",
  disabled = false,
  includeInheritOption = false,
  inheritLabel = "Heredar del cliente",
}) => {
  const { salesConditions, loading } = useSalesConditions();

  if (loading) {
    return <Skeleton className="h-10 w-full" />;
  }

  const handleChange = (newValue: string) => {
    onChange(newValue);
  };

  return (
    <div className={`space-y-2 ${className}`}>
      {label && <label className="text-sm font-medium">{label}</label>}
      <Select value={value} onValueChange={handleChange} disabled={disabled}>
        <SelectTrigger className="w-full">
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent>
          {includeInheritOption && (
            <SelectItem value="inherit">{inheritLabel}</SelectItem>
          )}
          {salesConditions.map((condition) => (
            <SelectItem key={condition.id} value={condition.id}>
              {renderSalesConditionLabel(condition)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
};

// Helper function to generate a human-readable label for each sales condition
export const renderSalesConditionLabel = (
  condition: SalesCondition
): string => {
  // Special case for specific named conditions
  if (condition.name === "ANTICIPADO") {
    return `${condition.name} (Pago anticipado)`;
  }

  if (condition.name.includes("CON. ENTREGA")) {
    return `${condition.name} (Pago tras entrega)`;
  }

  if (condition.name.startsWith("CREDITO")) {
    return `${condition.name} (Línea de crédito interna)`;
  }

  if (condition.name.startsWith("TELECHEQUE")) {
    return `${condition.name} (Plataforma externa)`;
  }

  if (condition.name.toLowerCase().startsWith("transferencia")) {
    return `${condition.name} (Línea de crédito interna)`;
  }

  // Handle by type if not a specific named condition
  switch (condition.type) {
    case "prepaid":
      if (condition.maximumDays === 0) {
        return `${condition.name} (Pago anticipado)`;
      }
      return `${condition.name} (Prepago)`;

    case "credit_line":
      return `${condition.name} (${condition.maximumDays ? ` ${condition.maximumDays} días` : ""})`;

    case "external_platform":
      if (condition.externalPlatformName) {
        return `${condition.name} (${condition.externalPlatformName}${condition.maximumDays ? ` - ${condition.maximumDays} días` : ""})`;
      }
      return `${condition.name} (${condition.maximumDays ? `${condition.maximumDays} días` : ""})`;

    default:
      return condition.name;
  }
};

export default SalesConditionSelector;
